import { useEffect, useState } from "react";
import { Button } from "../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../design/dialog";
import { Input } from "../design/input";
import {
  Field,
  Fieldset,
  FieldGroup,
  Label,
  ErrorMessage,
} from "../design/fieldset";
import { PlusIcon } from "@heroicons/react/20/solid";
import { useMatter } from "../contexts/MatterContext";
import { VisitationSchedule } from "../models/VisitationSchedule";
import { Select } from "../design/select";
import { VisitationScheduleStatus } from "../models/VisitationScheduleStatus";
import { VisitationScheduleType } from "../models/VisitationScheduleType";
import { VisitationScheduleInterval } from "../models/VisitationScheduleInterval";
import { VisitationRotationBlock } from "../models/VisitationRotationBlock";
import VisitationEditor from "./VisitationEditor";
import { generateRotationBlocks } from "../helpers/generateRotationBlocks";
import { Divider } from "../design/divider";
import { Subheading } from "../design/heading";
import { Participant } from "../models/Participant";
import { useVisitationSchedule } from "../contexts/VisitationScheduleContext";
import moment from "moment";

interface Props {
  schedule?: VisitationSchedule;
  children?: React.ReactNode;
  className?: string;
  onSave?: (id: VisitationSchedule["_id"]) => void;
}

interface Errors {
  saving?: string;
  name?: string;
  status?: string;
}

const VisitationScheduleButton: React.FC<Props> = ({
  schedule,
  children,
  className,
  onSave,
}) => {
  const { currentMatterId, currentMatter } = useMatter();
  const { createVisitationSchedule, updateVisitationSchedule } =
    useVisitationSchedule();

  const [participants, setParticipants] = useState<Participant[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [name, setName] = useState("");
  const [status, setStatus] = useState<VisitationScheduleStatus>("draft");
  const [type, setType] = useState<VisitationSchedule["type"]>("2-2-3");
  const [interval, setInterval] =
    useState<VisitationScheduleInterval>("1-week");
  const [startDate, setStartDate] = useState<string>(
    new Date().toISOString().split("T")[0],
  );
  const [endDate, setEndDate] = useState<string | undefined>(undefined);
  const [rotation, setRotation] = useState<{
    [key: number]: VisitationRotationBlock;
  }>({});
  const [errors, setErrors] = useState<Errors>({});

  // Load participants
  useEffect(() => {
    if (currentMatter?.me && currentMatter?.opponent) {
      setParticipants([currentMatter.me, currentMatter.opponent]);
    }
  }, [currentMatter]);

  // Load schedule on open
  useEffect(() => {
    if (schedule && isOpen) {
      setName(schedule.name || "");
      setStatus(schedule.status || "draft");
      setType(schedule.type || "2-2-3");
      setStartDate(moment(schedule.startDate).format("YYYY-MM-DD"));
      setEndDate(schedule.endDate);
      setInterval(schedule.interval || "1-week");
      setRotation(schedule.rotation || {});
    }
  }, [schedule, isOpen]);

  // Generate rotation when config changes
  useEffect(() => {
    if (!isOpen || participants.length !== 2) return;

    const newRotation = generateRotationBlocks(
      rotation,
      type,
      participants,
      interval,
    );
    setRotation(newRotation);
  }, [participants, interval, type, isOpen]);

  const validateForm = () => {
    const newErrors: Errors = {};
    if (!name.trim()) newErrors.name = "Name is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setIsSaving(true);
    setErrors({});
    try {
      if (schedule) {
        await updateVisitationSchedule(schedule._id, {
          name,
          type,
          status,
          startDate,
          endDate,
          rotation,
        });
        onSave?.(schedule._id);
      } else {
        // const id = await createVisitationSchedule(
        //   {
        //     name,
        //     type,
        //     status:
        //     startDate: new Date(startDate),
        //     endDate: endDate ? new Date(endDate) : undefined,
        //     children: [],
        //     interval,
        //     rotation,
        //   },
        // );
        // onSave?.(id);
      }
      setIsOpen(false);
    } catch (err: any) {
      setErrors((prev) => ({ ...prev, saving: err.message }));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        className={className}
        onClick={() => setIsOpen(true)}
      >
        {children ?? (
          <div className="flex items-center gap-2">
            {!schedule && <PlusIcon className="w-4 h-4" />}
            <span>{schedule ? "Edit Schedule" : "Create New Schedule"}</span>
          </div>
        )}
      </Button>

      <Dialog open={isOpen} onClose={setIsOpen}>
        <DialogTitle>
          {schedule ? "Edit Visitation Schedule" : "Create Visitation Schedule"}
        </DialogTitle>
        <DialogBody>
          {errors.saving && <ErrorMessage>{errors.saving}</ErrorMessage>}

          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Name</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Schedule Name"
                />
                {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
              </Field>

              {schedule && (
                <Field>
                  <Label>Status</Label>
                  <Select
                    value={status}
                    onChange={(e) =>
                      setStatus(e.target.value as VisitationScheduleStatus)
                    }
                  >
                    <option value="draft">Draft</option>
                    <option value="live">Live</option>
                  </Select>
                </Field>
              )}

              <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-4">
                <Field>
                  <Label>Schedule Type</Label>
                  <Select
                    value={type}
                    onChange={(e) =>
                      setType(e.target.value as VisitationScheduleType)
                    }
                  >
                    <option value="2-2-3">2-2-3</option>
                    <option value="2-2-5-5">2-2-5-5</option>
                    <option value="3-3-4-4">3-3-4-4</option>
                    <option value="alternating_weekends">
                      Alternating Weekends
                    </option>
                    <option value="5th_weekend">5th Weekend</option>
                    <option value="custom">Custom</option>
                  </Select>
                </Field>

                <Field>
                  <Label>Interval</Label>
                  <Select
                    value={interval}
                    onChange={(e) =>
                      setInterval(e.target.value as VisitationScheduleInterval)
                    }
                  >
                    <option value="1-week">1 Week</option>
                    <option value="2-weeks">2 Weeks</option>
                    <option value="3-weeks">3 Weeks</option>
                    <option value="4-weeks">4 Weeks</option>
                    <option value="5-weeks">5 Weeks</option>
                    <option value="6-weeks">6 Weeks</option>
                    <option value="7-weeks">7 Weeks</option>
                    <option value="8-weeks">8 Weeks</option>
                  </Select>
                </Field>
              </div>

              <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-4">
                <Field>
                  <Label>Start Date</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    disabled={isSaving}
                  />
                </Field>

                <Field>
                  <Label>End Date (optional)</Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    disabled={isSaving}
                  />
                </Field>
              </div>
            </FieldGroup>
          </Fieldset>

          <Divider className="my-4" />

          <Subheading>Visitation Schedule</Subheading>

          <VisitationEditor
            key={participants.map((p) => p._id).join(",")}
            startDate={startDate}
            rotation={rotation}
            setRotation={setRotation}
          />
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VisitationScheduleButton;
